/**
 * TranscriptStatsBar Component
 *
 * Renders the key-moments toggle above the transcript view. Word-count and
 * duration were removed because unreliable upstream duration data (e.g.
 * AssemblyAI returning sub-second values) produced a misleading "0 min"
 * display. If/when stable duration lands, add those stats back here.
 */

import { cn } from "@/lib/utils";

interface TranscriptStatsBarProps {
  /** Whether key moment data exists (shows toggle when true) */
  hasKeyMoments?: boolean;
  /** Current toggle state */
  showKeyMoments?: boolean;
  /** Callback when toggle is clicked */
  onToggleKeyMoments?: () => void;
}

export function TranscriptStatsBar({
  hasKeyMoments = false,
  showKeyMoments = false,
  onToggleKeyMoments,
}: TranscriptStatsBarProps) {
  if (!hasKeyMoments || !onToggleKeyMoments) {
    return null;
  }

  return (
    <div className="flex items-center justify-end mb-2">
      <button
        type="button"
        onClick={onToggleKeyMoments}
        className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
        aria-label={showKeyMoments ? "Hide key moments" : "Show key moments"}
      >
        <span className="hidden sm:inline">Key Moments</span>
        <div
          className={cn(
            "relative w-8 h-[18px] rounded-full transition-colors",
            showKeyMoments ? "bg-primary" : "bg-muted-foreground/30"
          )}
        >
          <div
            className={cn(
              "absolute top-[2px] w-[14px] h-[14px] rounded-full bg-card shadow-sm transition-all",
              showKeyMoments ? "left-[14px]" : "left-[2px]"
            )}
          />
        </div>
      </button>
    </div>
  );
}
